import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    TextField,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert,
    Autocomplete,
    SelectChangeEvent
} from '@mui/material';
import { useDataProvider, useNotify, useGetIdentity, useTranslate } from 'react-admin';
import { Groups as GroupsIcon, Business as DepartmentIcon, Person as PersonIcon } from '@mui/icons-material';

interface BulkEnrollmentDialogProps {
    open: boolean;
    onClose: () => void;
    courses: { id: number; title: string }[];
    initialCourseId: number | '';
    onSuccess: () => void;
}

const BulkEnrollmentDialog: React.FC<BulkEnrollmentDialogProps> = ({
    open,
    onClose,
    courses,
    initialCourseId,
    onSuccess
}) => {
    const [courseId, setCourseId] = useState<number | ''>(initialCourseId);
    const [tabValue, setTabValue] = useState(0);
    const [users, setUsers] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
    const [filters, setFilters] = useState<any>({});
    const [previewUsers, setPreviewUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [enrolling, setEnrolling] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();
    const { data: identity } = useGetIdentity();

    useEffect(() => {
        if (open) {
            setCourseId(initialCourseId);
            loadData();
        }
    }, [open]);

    const loadData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');

            // Load users
            const usersResponse = await fetch(`${process.env.REACT_APP_SSO_URL}/api/users?company.id=${(identity as any)?.companyId}&itemsPerPage=1000&order[lastName]=asc`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' }
            });
            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                setUsers(usersData.member || usersData['hydra:member'] || []);
            }

            // Load departments
            const deptsResponse = await fetch(`${process.env.REACT_APP_SSO_URL}/api/departments?company.id=${(identity as any)?.companyId}&order[name]=asc`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' }
            });
            if (deptsResponse.ok) {
                const deptsData = await deptsResponse.json();
                setDepartments(deptsData.member || deptsData['hydra:member'] || []);
            }

            // Load groups
            const groupsResponse = await fetch(`${process.env.REACT_APP_SSO_URL}/api/user_groups?company.id=${(identity as any)?.companyId}&order[name]=asc`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' }
            });
            if (groupsResponse.ok) {
                const groupsData = await groupsResponse.json();
                setGroups(groupsData.member || groupsData['hydra:member'] || []);
            }
        } catch (error) {
            notify('Fehler beim Laden der Daten', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollments/preview-users`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userIds: selectedUsers.map(u => u.id),
                    departmentIds: selectedDepartments,
                    groupIds: selectedGroups,
                    filters
                })
            });

            if (response.ok) {
                const data = await response.json();
                setPreviewUsers(data.users || []);
                notify(`${data.count} Benutzer gefunden`, { type: 'info' });
            }
        } catch (error) {
            notify('Fehler beim Laden der Vorschau', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleEnroll = async () => {
        setEnrolling(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollments/bulk-enroll`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    courseId,
                    userIds: selectedUsers.map(u => u.id),
                    departmentIds: selectedDepartments,
                    groupIds: selectedGroups,
                    filters
                })
            });

            if (response.ok) {
                const data = await response.json();
                const errors: { error: string }[] = data.errors || [];
                if (errors.length > 0) {
                    notify(translate('enrollments.manage.bulkPartial', {
                        enrolled: data.enrolled,
                        skipped: data.skipped,
                        failed: errors.length,
                        reason: errors[0].error
                    }), { type: data.enrolled === 0 ? 'warning' : 'info' });
                } else {
                    notify(`${data.enrolled} Benutzer eingeschrieben, ${data.skipped} übersprungen`, { type: 'success' });
                }
                onSuccess();
                handleClose();
            } else {
                notify('Fehler bei der Einschreibung', { type: 'error' });
            }
        } catch (error) {
            notify('Fehler bei der Einschreibung', { type: 'error' });
        } finally {
            setEnrolling(false);
        }
    };

    const handleClose = () => {
        setCourseId('');
        setTabValue(0);
        setSelectedUsers([]);
        setSelectedDepartments([]);
        setSelectedGroups([]);
        setFilters({});
        setPreviewUsers([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{translate('enrollments.manage.bulkEnroll')}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth required sx={{ mt: 1, mb: 2 }}>
                    <InputLabel id="bulk-enroll-course-label">{translate('enrollments.manage.bulkCourse')}</InputLabel>
                    <Select
                        labelId="bulk-enroll-course-label"
                        value={courseId}
                        label={translate('enrollments.manage.bulkCourse')}
                        onChange={(e) => setCourseId(e.target.value as number)}
                    >
                        {courses.map((course) => (
                            <MenuItem key={course.id} value={course.id}>
                                {course.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tab label="Einzelne Benutzer" icon={<PersonIcon />} iconPosition="start" />
                    <Tab label="Nach Abteilung" icon={<DepartmentIcon />} iconPosition="start" />
                    <Tab label="Nach Gruppe" icon={<GroupsIcon />} iconPosition="start" />
                    <Tab label="Filter" />
                </Tabs>

                {tabValue === 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Button
                                variant={selectedUsers.length === users.length && users.length > 0 ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => {
                                    if (selectedUsers.length === users.length) {
                                        setSelectedUsers([]);
                                    } else {
                                        setSelectedUsers([...users]);
                                    }
                                }}
                            >
                                {selectedUsers.length === users.length && users.length > 0
                                    ? `Alle abwählen (${users.length})`
                                    : `Alle Benutzer auswählen (${users.length})`}
                            </Button>
                        </Box>
                        <Autocomplete
                            multiple
                            options={users}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                            value={selectedUsers}
                            onChange={(e, newValue) => setSelectedUsers(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Benutzer auswählen" placeholder="Suchen..." />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={`${option.firstName} ${option.lastName}`} {...getTagProps({ index })} />
                                ))
                            }
                        />
                    </Box>
                )}

                {tabValue === 1 && (
                    <Box>
                        <FormControl fullWidth>
                            <InputLabel>Abteilungen</InputLabel>
                            <Select
                                multiple
                                value={selectedDepartments}
                                onChange={(e) => setSelectedDepartments(e.target.value as number[])}
                                input={<OutlinedInput label="Abteilungen" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const dept = departments.find(d => d.id === value);
                                            return <Chip key={value} label={dept?.name} />;
                                        })}
                                    </Box>
                                )}
                            >
                                {departments.map((dept) => (
                                    <MenuItem key={dept.id} value={dept.id}>
                                        {dept.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Alle Benutzer in den ausgewählten Abteilungen werden eingeschrieben
                        </Typography>
                    </Box>
                )}

                {tabValue === 2 && (
                    <Box>
                        <FormControl fullWidth>
                            <InputLabel>Gruppen</InputLabel>
                            <Select
                                multiple
                                value={selectedGroups}
                                onChange={(e) => setSelectedGroups(e.target.value as number[])}
                                input={<OutlinedInput label="Gruppen" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const group = groups.find(g => g.id === value);
                                            return <Chip key={value} label={group?.name} sx={{ bgcolor: group?.color, color: 'white' }} />;
                                        })}
                                    </Box>
                                )}
                            >
                                {groups.map((group) => (
                                    <MenuItem key={group.id} value={group.id}>
                                        <Chip label={group.name} size="small" sx={{ bgcolor: group.color, color: 'white', mr: 1 }} />
                                        {group.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Alle Benutzer in den ausgewählten Gruppen werden eingeschrieben
                        </Typography>
                    </Box>
                )}

                {tabValue === 3 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="E-Mail enthält"
                            value={filters.email || ''}
                            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                        />
                        <TextField
                            label="Vorname enthält"
                            value={filters.firstName || ''}
                            onChange={(e) => setFilters({ ...filters, firstName: e.target.value })}
                        />
                        <TextField
                            label="Nachname enthält"
                            value={filters.lastName || ''}
                            onChange={(e) => setFilters({ ...filters, lastName: e.target.value })}
                        />
                        <TextField
                            label="Jobtitel enthält"
                            value={filters.jobTitle || ''}
                            onChange={(e) => setFilters({ ...filters, jobTitle: e.target.value })}
                        />
                    </Box>
                )}

                {previewUsers.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {previewUsers.length} Benutzer werden eingeschrieben
                        </Alert>
                        <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
                            {previewUsers.slice(0, 50).map((user) => (
                                <ListItem key={user.id}>
                                    <ListItemText
                                        primary={`${user.firstName} ${user.lastName}`}
                                        secondary={`${user.email}${user.department ? ` • ${user.department}` : ''}`}
                                    />
                                </ListItem>
                            ))}
                            {previewUsers.length > 50 && (
                                <ListItem>
                                    <ListItemText secondary={`... und ${previewUsers.length - 50} weitere`} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Abbrechen</Button>
                <Button onClick={handlePreview} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Vorschau'}
                </Button>
                <Button
                    onClick={handleEnroll}
                    variant="contained"
                    disabled={enrolling || !courseId || previewUsers.length === 0}
                    sx={{ bgcolor: '#e72956', '&:hover': { bgcolor: '#c21642' } }}
                >
                    {enrolling ? <CircularProgress size={24} /> : `${previewUsers.length} Benutzer einschreiben`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkEnrollmentDialog;
